import { Stack, Switch } from '@mui/material'
import SettingsBox from 'src/Elements/Data/SettingsBox'
import React, { useCallback, useMemo } from 'react'
import { useField, useFormikContext } from 'formik'
import { SettingsFormValues } from 'src/Elements/SystemSettings/Battery/types'
import { NumberField } from 'src/Libs/inputs/formik'
import { OptionalTooltipWrapper } from '@e3dc-react/shell/Elements/Tooltip'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

interface AdjustmentBoundaries<AdjustmentStatus> {
  max: number
  min: number
  status: AdjustmentStatus
  target: number
}

interface Props {
  label: string
  valueFieldName: string
  stateFieldName: string
  boundaries: AdjustmentBoundaries<number>
  disabled?: boolean
}

/**
 * Component to adjust the voltage of the battery
 */
const VoltageAdjustmentFieldSet: React.FC<Props> = ({ label, valueFieldName, stateFieldName, boundaries, disabled = false }) => {
  const [stateField] = useField<boolean>(stateFieldName)
  const { t } = useCustomTranslation()
  const { setFieldValue } = useFormikContext<SettingsFormValues>()
  const isActive = useMemo<boolean>(() => stateField.value, [stateField.value])

  const isUsageDisabled = useMemo<boolean>(
    () => disabled || (boundaries.min === boundaries.max && boundaries.min <= 0),
    [disabled, boundaries.min, boundaries.max]
  )

  const handleSwitchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFieldValue(stateFieldName, event.target.checked)
      if (event.target.checked) {
        setFieldValue(valueFieldName, boundaries.min)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [valueFieldName, boundaries?.min]
  )

  const min = useMemo(() => (isActive ? boundaries.min : 0), [boundaries?.min, isActive])
  const max = useMemo(() => (isActive ? boundaries.max : 0), [boundaries?.max, isActive])

  return (
    <SettingsBox
      disabled={disabled}
      title={label}
      suffix={
        <OptionalTooltipWrapper titleKey={isUsageDisabled ? t('systemSettings.battery.min-max-zero') : ''}>
          <Stack direction="row" alignItems="start" sx={{ width: '100%', justifyContent: 'flex-end' }}>
            <Switch onChange={handleSwitchChange} disabled={isUsageDisabled} checked={stateField.value} />
            <NumberField
              disabled={!isActive || isUsageDisabled}
              step={0.1}
              name={valueFieldName}
              min={min}
              max={max}
              suffix="V"
              isDecimal
              ignoreNonNumerics
            />
          </Stack>
        </OptionalTooltipWrapper>
      }
    />
  )
}

export default VoltageAdjustmentFieldSet
